/*
 * Fovory collection (sections/fovory-collection.liquid) and search results.
 * - Filters are always behind "Filters", so the grid keeps the full width: a drawer on the
 *   left from 1024px, a bottom sheet below. Choices are made first and applied with
 *   "Show pieces (N)"; N is fetched as the choices change, so it is the real result.
 * - Sort: a small menu; picking an option keeps the filters (it is the same form).
 * - Load more: fetches the next page of this section and appends the cards.
 * - Long lists (brands, categories): "Show all" and, for brands, a search box.
 * - Sizes written two ways by the supplier are one button with a hidden twin checkbox;
 *   both change together, and their chip removes both.
 * Shared by the collection and search pages (snippets/fovory-filter-group.liquid).
 */
(() => {
  const root = document.querySelector('[data-fv-cat]');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = 'true';

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const form = root.querySelector('[data-fv-flt-form]');
  const sortInput = root.querySelector('[data-fv-sort-input]');

  function submit() {
    if (!form) return;
    // Empty price fields would send "filter.v.price.gte=" and break the URL: drop them.
    form.querySelectorAll('[data-fv-flt-price]').forEach((i) => {
      i.disabled = i.value === '';
    });
    if (form.requestSubmit) form.requestSubmit();
    else form.submit();
  }

  // "Show pieces (N)": ask Shopify for the count of the choices made so far.
  const go = form && form.querySelector('.fv-flt__go[data-label]');
  let countTimer = null;
  let countCtl = null;
  function refreshCount() {
    if (!go || !root.dataset.section) return;
    clearTimeout(countTimer);
    countTimer = setTimeout(async () => {
      if (countCtl) countCtl.abort();
      countCtl = new AbortController();
      const params = new URLSearchParams();
      new FormData(form).forEach((v, k) => {
        if (v !== '') params.append(k, v);
      });
      params.set('section_id', root.dataset.section);
      go.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${form.action}?${params.toString()}`, { signal: countCtl.signal });
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const n = doc.querySelector('#FvCatCount b');
        if (n) go.textContent = go.dataset.label.replace('[n]', n.textContent.trim());
      } catch (err) {
        /* keep the last count */
      } finally {
        go.removeAttribute('aria-busy');
      }
    }, 250);
  }

  if (form) {
    form.addEventListener('change', (e) => {
      const t = e.target;
      if (t.matches('[data-fv-list-find]')) return;
      if (t.dataset.fvTwins) {
        form.querySelectorAll(`[data-fv-twin-of="${t.dataset.fvTwins}"]`).forEach((x) => {
          x.checked = t.checked;
        });
      }
      refreshCount();
    });
    form.addEventListener('submit', () => {
      form.querySelectorAll('[data-fv-flt-price]').forEach((i) => {
        i.disabled = i.value === '';
      });
    });
  }

  // Filters sheet below 1440px.
  const sheet = root.querySelector('[data-fv-flt]');
  const panel = sheet && sheet.querySelector('.fv-flt');
  const opener = root.querySelector('[data-fv-flt-open]');
  let hideTimer = null;

  function openSheet() {
    if (!sheet) return;
    clearTimeout(hideTimer);
    sheet.classList.add('is-shown');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    document.documentElement.classList.add('fv-so-open');
    opener.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => requestAnimationFrame(() => {
      sheet.classList.add('is-open');
      panel.focus({ preventScroll: true });
    }));
  }

  function closeSheet() {
    if (!sheet || !sheet.classList.contains('is-shown')) return;
    sheet.classList.remove('is-open');
    document.documentElement.classList.remove('fv-so-open');
    opener.setAttribute('aria-expanded', 'false');
    hideTimer = setTimeout(() => {
      sheet.classList.remove('is-shown');
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
    }, reduce.matches ? 0 : 320);
    opener.focus({ preventScroll: true });
  }

  if (opener) opener.addEventListener('click', openSheet);
  if (sheet) {
    sheet.addEventListener('click', (e) => {
      if (e.target.closest('[data-fv-flt-close]')) closeSheet();
    });
    sheet.addEventListener('keydown', (e) => {
      if (!sheet.classList.contains('is-shown')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeSheet();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = [...panel.querySelectorAll('a[href], button, input:not([disabled])')].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
    let startY = null;
    panel.addEventListener('touchstart', (e) => {
      startY = panel.scrollTop <= 0 ? e.touches[0].clientY : null;
    }, { passive: true });
    panel.addEventListener('touchend', (e) => {
      if (startY !== null && e.changedTouches[0].clientY - startY > 80) closeSheet();
      startY = null;
    }, { passive: true });
  }

  // Sort menu.
  const sortBtn = root.querySelector('[data-fv-sort]');
  const sortMenu = sortBtn && document.getElementById(sortBtn.getAttribute('aria-controls'));
  if (sortBtn && sortMenu) {
    const setOpen = (open) => {
      sortBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      sortMenu.hidden = !open;
    };
    sortBtn.addEventListener('click', () => {
      const open = sortBtn.getAttribute('aria-expanded') !== 'true';
      setOpen(open);
      if (open) {
        const cur = sortMenu.querySelector('[aria-current="true"]') || sortMenu.querySelector('button');
        if (cur) cur.focus();
      }
    });
    sortMenu.addEventListener('click', (e) => {
      const o = e.target.closest('[data-sort-value]');
      if (!o) return;
      setOpen(false);
      if (o.getAttribute('aria-current') === 'true') {
        sortBtn.focus();
        return;
      }
      if (sortInput) sortInput.value = o.dataset.sortValue;
      submit();
    });
    sortMenu.addEventListener('keydown', (e) => {
      const opts = [...sortMenu.querySelectorAll('button')];
      const i = opts.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); opts[(i + 1) % opts.length].focus(); }
      if (e.key === 'ArrowUp') { e.preventDefault(); opts[(i - 1 + opts.length) % opts.length].focus(); }
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false); sortBtn.focus(); }
    });
    document.addEventListener('click', (e) => {
      if (!sortMenu.hidden && !e.target.closest('.fv-sort')) setOpen(false);
    });
  }

  // Long lists (brands, categories, a long run of sizes): "Show all", and the brand search box.
  // Each list sits in a box with its own toggle, search box and "no match" line.
  root.querySelectorAll('[data-fv-list]').forEach((list) => {
    const box = list.parentElement;
    const toggle = box.querySelector(':scope > [data-fv-list-toggle]');
    const find = box.querySelector(':scope > .fv-flt__find [data-fv-list-find]');
    const miss = box.querySelector(':scope > [data-fv-list-miss]');
    if (toggle) {
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggle.textContent = open ? toggle.dataset.less : toggle.dataset.more;
        list.classList.toggle('is-all', open);
      });
    }
    if (find) {
      const items = [...list.querySelectorAll('.fv-chk')];
      const norm = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
      const names = items.map((it) => norm(it.querySelector('span').textContent));
      find.addEventListener('input', () => {
        const q = norm(find.value);
        let shown = 0;
        items.forEach((it, i) => {
          const hit = !q || names[i].includes(q);
          it.classList.toggle('is-miss', !hit);
          if (hit) shown += 1;
        });
        list.classList.toggle('is-finding', q !== '');
        if (miss) miss.hidden = shown > 0;
        if (toggle) toggle.hidden = q !== '';
      });
      find.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') e.preventDefault();
        if (e.key === 'Escape' && find.value) {
          e.preventDefault();
          e.stopPropagation();
          find.value = '';
          find.dispatchEvent(new Event('input'));
        }
      });
    }
  });

  // A chip for a size with two spellings removes both.
  root.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-fv-chip-values]');
    if (!chip) return;
    e.preventDefault();
    const url = new URL(window.location.href);
    const name = chip.dataset.fvChipName;
    const drop = chip.dataset.fvChipValues.split('|').filter(Boolean);
    const keep = url.searchParams.getAll(name).filter((v) => !drop.includes(v));
    url.searchParams.delete(name);
    keep.forEach((v) => url.searchParams.append(name, v));
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  // Load more.
  const grid = root.querySelector('[data-fv-grid]');
  root.addEventListener('click', async (e) => {
    const more = e.target.closest('[data-fv-more]');
    if (!more || !grid) return;
    e.preventDefault();
    if (more.getAttribute('aria-busy') === 'true') return;
    more.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(more.href, location.href);
      url.searchParams.set('section_id', root.dataset.section);
      const html = await fetch(url.toString()).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = doc.querySelectorAll('[data-fv-grid] > li');
      const firstNew = items[0];
      items.forEach((li) => grid.appendChild(document.importNode(li, true)));
      const total = parseInt(more.dataset.total, 10) || 0;
      const seen = grid.children.length;
      const seenEl = root.querySelector('[data-fv-seen]');
      const bar = root.querySelector('[data-fv-bar]');
      if (seenEl) seenEl.textContent = seen;
      if (bar && total) bar.style.width = Math.min(100, Math.round((seen / total) * 100)) + '%';
      const next = doc.querySelector('[data-fv-more]');
      if (next) more.href = next.getAttribute('href');
      else more.remove();
      // Keep keyboard users in place: focus the first new card's link.
      const added = grid.children[seen - items.length];
      const link = added && added.querySelector('a[href]');
      if (link && firstNew) link.focus({ preventScroll: true });
      // Let the theme wire up hearts and quick add on the new cards.
      document.dispatchEvent(new CustomEvent('fovory:cards-added'));
      if (window.MinimogTheme && MinimogTheme.Wishlist && MinimogTheme.Wishlist.init) MinimogTheme.Wishlist.init();
    } catch (err) {
      window.location.href = more.href;
    } finally {
      more.removeAttribute('aria-busy');
    }
  });
})();
